import {
  ElementStatus,
  PageStructure,
  SelectedElementContainer,
  ToolMessage,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { toDragPointerEvent, useUpdateBounds } from '@st/document-viewer-kit/src/use-movable'
import { StampAnnotation } from '@st/sdk'
import { THEME_COLORS } from '@st/theme/colors'
import { uuidv7 } from '@st/util/uuidv7'
import { useMemo, useState } from 'react'
import { CheckmarkToolState } from '~/st-doc-viewer/annotations'
import { InlineColorPicker } from '~/theme'
import { CHECKMARK_TICK_STAMP } from '../stamp/templates'
import { recomputeStampLayoutAndStyle } from '../stamps-core/stamp-layout'
import { StampIconDisplay } from '../stamps-core/stamp-display'
import { AnnotationFloatingToolbar, FloatingToolbarButton } from '~/st-doc-viewer/floating-toolbar'
import { FontSizeLargerIcon, FontSizeSmallerIcon } from '@st/theme'
import { DeleteIcon } from '~/components/icons'

export const CHECKMARK_COLOR_OPTIONS = [
  THEME_COLORS['red'][600],
  THEME_COLORS['blue'][500],
  THEME_COLORS['orange'][500],
  THEME_COLORS['green'][500],
  THEME_COLORS['gray'][900]
]

export function CheckmarkToolEditor({
  toolState,
  activeAnnotation,
  send
}: {
  toolState: CheckmarkToolState
  activeAnnotation: StampAnnotation | undefined
  send: (message: ToolMessage<StampAnnotation, CheckmarkToolState>) => void
}) {
  return (
    <InlineColorPicker
      options={CHECKMARK_COLOR_OPTIONS}
      value={toolState.settings.color}
      onChange={(color) => {
        if (activeAnnotation) {
          const newAnnotation: StampAnnotation = {
            ...activeAnnotation,
            color
          }
          send({ type: 'updateAnnotationDraft', annotationDraft: newAnnotation })
          send({ type: 'updateTool', tool: syncToolStateWithAnnotation(newAnnotation) })
        } else {
          send({
            type: 'updateTool',
            tool: {
              name: 'checkmark',
              settings: {
                ...toolState.settings,
                color
              }
            }
          })
        }
      }}
    />
  )
}

export function syncToolStateWithAnnotation(annotation: StampAnnotation): CheckmarkToolState {
  return {
    name: 'checkmark',
    settings: {
      templateId: CHECKMARK_TICK_STAMP.id,
      color: annotation.color,
      stampSize: annotation.stampSize
    }
  }
}

function selEffectiveAnnotation(settings: CheckmarkToolState['settings']) {
  return recomputeStampLayoutAndStyle({
    ...CHECKMARK_TICK_STAMP.annotation,
    color: settings.color,
    stampSize: settings.stampSize
  })
}

export function CheckmarkToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: CheckmarkToolState
  activeAnnotation: StampAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, CheckmarkToolState>) => void
}) {
  const transform = useLinearTransform()
  const [pointer, setPointer] = useState<{ x: number; y: number } | undefined>(undefined)

  const ghostAnnotation = useMemo(() => {
    return selEffectiveAnnotation(toolState.settings)
  }, [toolState.settings])

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          backgroundColor: 'transparent'
        }}
        onPointerMove={(e) => {
          if (activeAnnotation) {
            setPointer(undefined)
            return
          }
          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')
          setPointer(pointerEvent.point)
        }}
        onPointerLeave={() => setPointer(undefined)}
        onPointerDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')
          if (activeAnnotation) {
            send({ type: 'deselectAnnotation' })
            return
          }
          const baseAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
            ...selEffectiveAnnotation(toolState.settings),
            id: uuidv7(),
            time: new Date().toISOString(),
            type: 'stamp',
            pageNumber: page.pageNumber,
            bounds: { x: pointerEvent.point.x, y: pointerEvent.point.y, width: 0, height: 0 }
          })
          const annotation: StampAnnotation = {
            ...baseAnnotation,
            bounds: {
              width: baseAnnotation.bounds.width,
              height: baseAnnotation.bounds.height,
              x: baseAnnotation.bounds.x - baseAnnotation.bounds.width / 2,
              y: baseAnnotation.bounds.y - baseAnnotation.bounds.height / 2
            }
          }
          send({
            type: 'createAnnotationDraft',
            elementStatus: 'none',
            annotationDraft: annotation
          })
          send({ type: 'createAnnotation', annotationDraft: annotation })
        }}
      />
      {ghostAnnotation && pointer && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            pointerEvents: 'none',
            transform: `translate(${pointer.x}px, ${pointer.y}px)`
          }}
        >
          <div style={{ transform: 'translate(-50%, -50%)' }}>
            <StampIconDisplay annotation={ghostAnnotation} />
          </div>
        </div>
      )}
    </>
  )
}

export function CheckmarkAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: StampAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<StampAnnotation, CheckmarkToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      return <CheckmarkAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <ViewElementContainer
          key={annotation.id}
          bounds={annotation.bounds}
          onClick={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        >
          <StampIconDisplay annotation={annotation} />
        </ViewElementContainer>
      )
  }
}

function CheckmarkAnnotationSelected({
  annotation,
  send
}: {
  annotation: StampAnnotation
  send: (message: ToolMessage<StampAnnotation, CheckmarkToolState>) => void
}) {
  const { onMove } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      onMove={onMove}
      resizable={false}
      toolbar={
        <AnnotationFloatingToolbar>
          <FloatingToolbarButton
            icon={<FontSizeSmallerIcon className="size=6" />}
            onClick={() => {
              const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                ...annotation,
                stampSize: annotation.stampSize - 2
              })

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: syncToolStateWithAnnotation(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<FontSizeLargerIcon className="size-6" />}
            onClick={() => {
              const newAnnotation: StampAnnotation = recomputeStampLayoutAndStyle({
                ...annotation,
                stampSize: annotation.stampSize + 2
              })

              send({
                type: 'updateAnnotationDraft',
                annotationDraft: newAnnotation
              })
              send({ type: 'updateTool', tool: syncToolStateWithAnnotation(newAnnotation) })
            }}
          />
          <FloatingToolbarButton
            icon={<DeleteIcon className="size-6" />}
            onClick={() => {
              send({ type: 'deleteAnnotation', annotationId: annotation.id })
            }}
          />
        </AnnotationFloatingToolbar>
      }
    >
      <StampIconDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}
